/* eslint-disable react-hooks/refs */
import type { ComponentProps, HTMLProps } from 'react';
import type React from 'react';

import { FloatingArrow, FloatingPortal } from '@floating-ui/react';

import { Box } from '@change-corgi/design-system-legacy/layout';
import { useColor, useSunriseColor } from '@change-corgi/design-system-legacy/theme';

import { useTooltipContext } from '../hooks/context';

type TooltipContentProps = {
	colorScheme?: 'light' | 'dark';
};

export const TooltipContent: React.FC<ComponentProps<typeof Box> & TooltipContentProps> = ({
	colorScheme = 'dark',
	children,
	...props
}) => {
	const context = useTooltipContext();

	const { isSunriseEnabled } = context;
	const backgroundColor = colorScheme === 'dark' ? 'primary-black' : 'background';
	const textColor = colorScheme === 'dark' ? 'typography-lightPrimary' : 'typography-primary';
	const arrowColor = useColor(backgroundColor);
	const sunriseArrowColor = useSunriseColor('sunrise.semantic.stroke.info.weak');

	if (!context.open) return null;

	return (
		<FloatingPortal>
			<Box
				ref={context.refs.setFloating}
				sx={{
					...context.floatingStyles,
					minWidth: 'min-content',
					maxWidth: '400px',
					p: 16,
					borderRadius: 12,
					backgroundColor,
					color: textColor,
					...(isSunriseEnabled && {
						backgroundColor: 'black',
						color: 'white',
						borderWidth: '1px',
						borderColor: 'sunrise.semantic.stroke.info.weak',
						borderStyle: 'solid',
						borderRadius: 8,
					}),
				}}
				{...context.getFloatingProps(props as HTMLProps<HTMLElement>)}
				aria-live="polite"
			>
				<FloatingArrow
					ref={context.arrowRef}
					context={context.context}
					fill={isSunriseEnabled ? sunriseArrowColor : arrowColor}
				/>
				{children}
			</Box>
		</FloatingPortal>
	);
};
